export enum BaseWebSocketErrorCode {
  Disconnection = 4002,
  UnknownError = 4003,
}

export enum BaseWebsocketServerToClientMessageType {
  Ping = 'ping',

  ServerError = 'server-error',
  PeerId = 'peer-id',
  PeerConnected = 'peer-connected',
  PeerDisconnected = 'peer-disconnected',
  SetPeers = 'set-peers',

  PeerMessage = 'peer-message',
}

export enum BaseServerDisconnectReason {
  Unknown = 'unknown',

  Error = 'error',

  Timeout = 'timeout',
  RoomClosed = 'room-closed',
}

export type BaseServerDisconnectErrorReasonMessage = {
  type: BaseServerDisconnectReason.Error;
  error: BaseWebsocketServerErrorMessage;
};

export type BaseServerDisconnectReasonMessage =
  | {
      type: Exclude<BaseServerDisconnectReason, BaseServerDisconnectReason.Error>;
    }
  | BaseServerDisconnectErrorReasonMessage;

export const isServerDisconnectReasonMessage = (
  payload: unknown,
): payload is BaseServerDisconnectReasonMessage => {
  if (typeof payload !== 'object' || payload === null) {
    return false;
  }

  if (!('type' in payload) || typeof payload.type !== 'string') {
    return false;
  }

  return true;
};

export type BaseWebsocketServerToClientPingMessage = {
  type: BaseWebsocketServerToClientMessageType.Ping;
  latency: number | undefined;
};

export type BaseWebsocketServerToClientPeerIdMessage = {
  type: BaseWebsocketServerToClientMessageType.PeerId;
  peerId: string;
};

export type BaseWebsocketServerToClientPeerMessage<Message> = {
  type: BaseWebsocketServerToClientMessageType.PeerMessage;
  peerId: string;
  message: Message;
};

export enum BaseErrorCodes {
  Unauthorized = 'Unauthorized',
  InvalidAuthorizationRequest = 'InvalidAuthorizationRequest',
  UnknownAuthorizationError = 'UnknownAuthorizationError',
  ServerClosing = 'ServerClosing',
  UnknownError = 'UnknownError',
  NoRoomFound = 'NoRoomFound',
  InvalidOrigin = 'InvalidOrigin',
  InvalidRoomId = 'InvalidRoomId',
  InvalidTargetId = 'InvalidTargetId',
}

export type BaseWebsocketServerErrorMessage = {
  type: BaseWebsocketServerToClientMessageType.ServerError;
  code: BaseErrorCodes;
};

export const isBaseWebsocketServerErrorMessage = (
  payload: unknown,
): payload is BaseWebsocketServerErrorMessage => {
  if (typeof payload !== 'object' || payload === null) {
    return false;
  }

  if (!('type' in payload) || typeof payload.type !== 'string') {
    return false;
  }

  if (!('code' in payload) || typeof payload.code !== 'string') {
    return false;
  }

  return true;
};

export type BaseWebsocketServerToClientPeerConnectedMessage<Metadata = unknown> = {
  type: BaseWebsocketServerToClientMessageType.PeerConnected;
  peerId: string;
  metadata: Metadata;
};

export type BaseWebsocketServerToClientPeersConnectedMessage<Metadata = unknown> = {
  type: BaseWebsocketServerToClientMessageType.SetPeers;
  peerIds: string[];
  metadata: Metadata[];
};

export type BaseWebsocketServerToClientPeerDisconnectedMessage<Metadata = unknown> = {
  type: BaseWebsocketServerToClientMessageType.PeerDisconnected;
  peerId: string;
  metadata: Metadata;
};

export type BaseWebsocketServerToClientMessage<
  P2pMessage = unknown,
  Metadata = unknown,
> =
  | BaseWebsocketServerToClientPeerMessage<P2pMessage>
  | BaseWebsocketServerErrorMessage
  | BaseWebsocketServerToClientPingMessage
  | BaseWebsocketServerToClientPeerIdMessage
  | BaseWebsocketServerToClientPeerConnectedMessage<Metadata>
  | BaseWebsocketServerToClientPeersConnectedMessage<Metadata>
  | BaseWebsocketServerToClientPeerDisconnectedMessage<Metadata>;

export const isBaseWebsocketServerToClientMessage = <
  P2pMessage = any,
  Metadata = unknown,
>(
  payload: unknown,
): payload is BaseWebsocketServerToClientMessage<P2pMessage, Metadata> => {
  if (typeof payload !== 'object' || payload === null) {
    return false;
  }

  if (!('type' in payload) || typeof payload.type !== 'string') {
    return false;
  }

  const validTypes = Object.values(BaseWebsocketServerToClientMessageType);

  return validTypes.includes(payload.type as any);
};
